import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import type { Member, WorkspaceSettings } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { ChatAppearance } from '../components/ChatAppearance.js';
import { SpecialMessages } from '../components/SpecialMessages.js';
import { Alert } from '../components/Alert.js';
import { EmptyState } from '../components/EmptyState.js';
import { Field } from '../components/Field.js';
import { StatusPill } from '../components/StatusPill.js';
import { RetentionPanel } from '../components/RetentionPanel.js';
import { WorkspaceHelpDialog } from '../components/WorkspaceHelpDialog.js';

/**
 * Workspace settings and members (ARCHITECTURE.md §10, §64).
 *
 * Controls the actor cannot use are not rendered. That is a courtesy, not a
 * control: §4.2 keeps the server authoritative and every request is re-checked,
 * so hiding a button prevents confusion rather than privilege.
 */
export function SettingsPage(): React.JSX.Element {
  const { can, refresh } = useSession();

  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const canManage = can('settings.manage');
  const canManageMembers = can('members.manage');

  const load = useCallback(async (): Promise<void> => {
    setLoadError(null);
    try {
      const next = await apiRequest<WorkspaceSettings>('/settings');
      setSettings(next);
      setName(next.workspace.name);
      setTimezone(next.timezone);
    } catch (error) {
      setLoadError(
        error instanceof ApiError ? error.message : 'Could not load workspace settings.',
      );
      return;
    }

    if (canManageMembers) {
      try {
        const response = await apiRequest<{ members: Member[] }>('/members');
        setMembers(response.members);
      } catch {
        // A member-list failure must not blank the whole page.
        setMembers(null);
      }
    }
  }, [canManageMembers]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setSaveError(null);
    setSaved(false);
    setSaving(true);
    try {
      await apiRequest('/settings', { method: 'PATCH', body: { name, timezone } });
      setSaved(true);
      // The workspace name appears in the switcher, so refresh session state.
      await refresh();
      await load();
    } catch (error) {
      setSaveError(error instanceof ApiError ? error : null);
    } finally {
      setSaving(false);
    }
  };

  if (loadError !== null) {
    return (
      <section className="panel">
        <div className="panel__body">
          <Alert tone="error">{loadError}</Alert>
        </div>
      </section>
    );
  }

  if (settings === null) {
    return (
      <p role="status" aria-live="polite">
        Loading settings…
      </p>
    );
  }

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Settings</h1>
        <p className="page__subtitle">Configuration for {settings.workspace.name}.</p>
      </header>

      <div className="grid">
        <section className="panel" aria-labelledby="workspace-settings-heading">
          <div className="panel__header">
            <h2 className="panel__title" id="workspace-settings-heading">
              Workspace
            </h2>
            <span className="panel__actions">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setHelpOpen(true);
                }}
              >
                How workspaces work
              </button>
              <StatusPill tone="idle" label={settings.role} />
            </span>
          </div>
          <div className="panel__body">
            {canManage ? (
              <form
                onSubmit={(event) => {
                  void handleSave(event);
                }}
                noValidate
              >
                {saveError === null ? null : <Alert tone="error">{saveError.message}</Alert>}
                {saved ? <Alert tone="success">Settings saved.</Alert> : null}

                <Field
                  label="Workspace name"
                  value={name}
                  onChange={setName}
                  required
                  error={saveError?.fieldError('name')}
                />
                <Field
                  label="Time zone"
                  value={timezone}
                  onChange={setTimezone}
                  hint="IANA name, for example Europe/London. Timestamps are stored in UTC and displayed in this zone."
                  required
                  error={saveError?.fieldError('timezone')}
                />

                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </form>
            ) : (
              <>
                <dl className="definition">
                  <dt>Name</dt>
                  <dd>{settings.workspace.name}</dd>
                  <dt>Identifier</dt>
                  <dd>
                    <code style={{ fontFamily: 'var(--font-mono)' }}>
                      {settings.workspace.slug}
                    </code>
                  </dd>
                  <dt>Time zone</dt>
                  <dd>{settings.timezone}</dd>
                </dl>
                <p
                  style={{
                    marginBottom: 0,
                    fontSize: 'var(--text-xs)',
                    color: 'var(--c-text-muted)',
                  }}
                >
                  Your role ({settings.role}) can view these settings but not change them.
                </p>
              </>
            )}
          </div>
        </section>

        <ChatAppearance />

        <SpecialMessages />

        {canManageMembers ? (
          <section className="panel" aria-labelledby="members-heading">
            <div className="panel__header">
              <h2 className="panel__title" id="members-heading">
                Members
              </h2>
            </div>
            <div className="panel__body" style={{ padding: 0 }}>
              {members === null || members.length === 0 ? (
                <EmptyState title="No members to show">
                  Member management is available, but inviting teammates by email arrives with
                  workspace invitations.
                </EmptyState>
              ) : (
                <table className="table">
                  <caption className="visually-hidden">
                    People with access to this workspace
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.membershipId}>
                        <td>{member.displayName}</td>
                        <td>{member.email}</td>
                        <td>{member.role}</td>
                        <td>
                          <StatusPill
                            tone={member.status === 'ACTIVE' ? 'ok' : 'warn'}
                            label={member.status}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        ) : null}
        <RetentionPanel />
      </div>

      <WorkspaceHelpDialog
        open={helpOpen}
        onClose={() => {
          setHelpOpen(false);
        }}
      />
    </>
  );
}
